import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Slider,
  TextField,
  Button,
  Chip,
  IconButton,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useJediReleasesGet, useJediReleaseUpdate, JediRelease } from '../../../clients/graphql/pages/release-queries';

export const ReleaseDetailPage = () => {
  const { releaseKey } = useParams<{ releaseKey: string }>();
  const navigate = useNavigate();
  const { loading, error, data } = useJediReleasesGet({});
  const [updateRelease] = useJediReleaseUpdate();
  const [updating, setUpdating] = useState(false);
  const [newUserKey, setNewUserKey] = useState('');
  const [localRelease, setLocalRelease] = useState<JediRelease | null>(null);
  const [localPercentage, setLocalPercentage] = useState<number>(0);
  const [localType, setLocalType] = useState<string>('');

  const releases = data?.jediReleasesGet?.releases || [];
  const release = releases.find((r: JediRelease) => r.releaseKey === releaseKey);

  useEffect(() => {
    if (release) {
      setLocalRelease(release);
      setLocalPercentage(release.percentage || 0);
      setLocalType(release.type || '');
    }
  }, [release]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !release) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
        <Alert severity="error">
          Release not found or error fetching data.
        </Alert>
      </Box>
    );
  }

  const handleBack = () => {
    navigate('/developer/release');
  };

  const handleEnabledChange = async (enabled: boolean) => {
    if (!localRelease) return;
    
    setUpdating(true);
    try {
      await updateRelease({
        variables: {
          input: {
            name: localRelease.releaseKey,
            enabled: enabled,
          },
        },
      });
      setLocalRelease({ ...localRelease, enabled });
    } catch (error) {
      console.error('Error updating release:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handlePercentageChange = (percentage: number) => {
    setLocalPercentage(percentage);
  };

  const handleSavePercentage = async () => {
    if (!localRelease) return;
    
    setUpdating(true);
    try {
      await updateRelease({
        variables: {
          input: {
            name: localRelease.releaseKey,
            newPercentage: localPercentage,
          },
        },
      });
      setLocalRelease({ ...localRelease, percentage: localPercentage });
    } catch (error) {
      console.error('Error updating percentage:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveType = async () => {
    if (!localRelease) return;
    
    setUpdating(true);
    try {
      const input: any = {
        name: localRelease.releaseKey,
        newType: localType,
      };
      
      // Set percentage to null if type is not PERCENTAGE
      if (localType !== 'PERCENTAGE') {
        input.newPercentage = null;
      }
      
      await updateRelease({
        variables: {
          input,
        },
      });
      
      // Update local state
      const updatedRelease = { ...localRelease, type: localType };
      if (localType !== 'PERCENTAGE') {
        updatedRelease.percentage = 0;
        setLocalPercentage(0);
      }
      setLocalRelease(updatedRelease);
    } catch (error) {
      console.error('Error updating type:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAddToWhitelist = async () => {
    if (!localRelease || !newUserKey.trim()) return;
    
    const currentWhitelist = localRelease.whitelist || [];
    const updatedWhitelist = [...currentWhitelist, newUserKey.trim()];
    
    setUpdating(true);
    try {
      await updateRelease({
        variables: {
          input: {
            name: localRelease.releaseKey,
            newWhitelist: updatedWhitelist,
          },
        },
      });
      setLocalRelease({ ...localRelease, whitelist: updatedWhitelist });
      setNewUserKey('');
    } catch (error) {
      console.error('Error updating whitelist:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleRemoveFromWhitelist = async (userKeyToRemove: string) => {
    if (!localRelease) return;
    
    const currentWhitelist = localRelease.whitelist || [];
    const updatedWhitelist = currentWhitelist.filter((key: string) => key !== userKeyToRemove);
    
    setUpdating(true);
    try {
      await updateRelease({
        variables: {
          input: {
            name: localRelease.releaseKey,
            newWhitelist: updatedWhitelist,
          },
        },
      });
      setLocalRelease({ ...localRelease, whitelist: updatedWhitelist });
    } catch (error) {
      console.error('Error updating whitelist:', error);
    } finally {
      setUpdating(false);
    }
  };

  const whitelistArray = localRelease?.whitelist || [];


  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, px: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4">
          Release: {releaseKey}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Release Info Card */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Release Information
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Created: {new Date(localRelease?.createdAt || '').toLocaleString()}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Updated: {new Date(localRelease?.updatedAt || '').toLocaleString()}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Release Type
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={localType}
                    onChange={(e) => setLocalType(e.target.value)}
                    label="Type"
                    disabled={updating}
                  >
                    <MenuItem value="WHITELIST">WHITELIST</MenuItem>
                    <MenuItem value="BETA">BETA</MenuItem>
                    <MenuItem value="INTERNAL">INTERNAL</MenuItem>
                    <MenuItem value="PERCENTAGE">PERCENTAGE</MenuItem>
                    <MenuItem value="FEATURE_FLAG">FEATURE_FLAG</MenuItem>
                  </Select>
                </FormControl>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <Button
                    variant="outlined"
                    onClick={handleSaveType}
                    disabled={updating || localType === localRelease?.type}
                    size="small"
                  >
                    Save Type
                  </Button>
                </Box>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={localRelease?.enabled || false}
                    onChange={(e) => handleEnabledChange(e.target.checked)}
                    disabled={updating}
                  />
                }
                label="Enabled"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Percentage Control Card */}
        {localRelease?.type === 'PERCENTAGE' && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Percentage Control
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current: {localPercentage}%
                </Typography>
                <Box sx={{ px: 2, py: 2 }}>
                  <Slider
                    value={localPercentage}
                    onChange={(_, value) => handlePercentageChange(value as number)}
                    min={0}
                    max={100}
                    step={1}
                    marks={[
                      { value: 0, label: '0%' },
                      { value: 50, label: '50%' },
                      { value: 100, label: '100%' },
                    ]}
                    disabled={updating}
                    valueLabelDisplay="auto"
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Button
                    variant="contained"
                    onClick={handleSavePercentage}
                    disabled={updating || localPercentage === (localRelease?.percentage || 0)}
                    size="small"
                  >
                    Save Percentage
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Whitelist Control Card */}
        {localRelease?.type === 'WHITELIST' && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Whitelist Control
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <TextField
                    label="Add User Key"
                    value={newUserKey}
                    onChange={(e) => setNewUserKey(e.target.value)}
                    size="small"
                    sx={{ flexGrow: 1 }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddToWhitelist();
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleAddToWhitelist}
                    disabled={!newUserKey.trim() || updating}
                    startIcon={<AddIcon />}
                  >
                    Add
                  </Button>
                </Box>

                <Paper sx={{ p: 2, minHeight: 100 }}>
                  {whitelistArray.length > 0 ? (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {whitelistArray.map((userKey) => (
                        <Chip
                          key={userKey}
                          label={userKey}
                          onDelete={() => handleRemoveFromWhitelist(userKey)}
                          deleteIcon={<DeleteIcon />}
                          disabled={updating}
                        />
                      ))}
                    </Box>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No users in whitelist
                    </Typography>
                  )}
                </Paper>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {updating && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
    </Box>
  );
}; 